import React, { useEffect, useState, useCallback } from 'react';
import { useServiceOS } from '../hooks/useServiceOS';
import {
  CalendarCheck,
  CheckCircle,
  XCircle,
  Clock,
  MapPin,
  User,
  Plus,
} from '@phosphor-icons/react';

const STATUS_COLORS = {
  scheduled: 'bg-[#6366F1]/10 text-[#6366F1]',
  completed: 'bg-[#22C55E]/10 text-[#22C55E]',
  cancelled: 'bg-[#EF4444]/10 text-[#EF4444]',
};

function NewJobModal({ open, onClose, onSubmit }) {
  const [form, setForm] = useState({
    customer_name: '', customer_email: '', service_type: '', location_text: '',
    scheduled_date: new Date().toISOString().split('T')[0], start_time: '09:00', end_time: '10:00',
    duration_minutes: 60, notes: '',
  });
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit(form);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="new-job-modal">
      <div className="bg-[#12131A] border border-[#1E1F2E] rounded-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold text-white mb-4">New job</h2>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs text-[#8B8FA3] mb-1">Customer name *</label>
            <input
              value={form.customer_name}
              onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))}
              className="w-full px-3 py-2 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-sm text-white focus:outline-none focus:border-[#14F195]/50"
              required
              data-testid="job-customer-name"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-[#8B8FA3] mb-1">Date *</label>
              <input
                type="date"
                value={form.scheduled_date}
                onChange={e => setForm(f => ({ ...f, scheduled_date: e.target.value }))}
                className="w-full px-3 py-2 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-sm text-white focus:outline-none focus:border-[#14F195]/50"
                required
                data-testid="job-date"
              />
            </div>
            <div>
              <label className="block text-xs text-[#8B8FA3] mb-1">Service</label>
              <input
                value={form.service_type}
                onChange={e => setForm(f => ({ ...f, service_type: e.target.value }))}
                className="w-full px-3 py-2 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-sm text-white focus:outline-none focus:border-[#14F195]/50"
                data-testid="job-service"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-[#8B8FA3] mb-1">Start time</label>
              <input
                type="time"
                value={form.start_time}
                onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))}
                className="w-full px-3 py-2 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-sm text-white focus:outline-none focus:border-[#14F195]/50"
                data-testid="job-start"
              />
            </div>
            <div>
              <label className="block text-xs text-[#8B8FA3] mb-1">End time</label>
              <input
                type="time"
                value={form.end_time}
                onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))}
                className="w-full px-3 py-2 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-sm text-white focus:outline-none focus:border-[#14F195]/50"
                data-testid="job-end"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-[#8B8FA3] mb-1">Location</label>
            <input
              value={form.location_text}
              onChange={e => setForm(f => ({ ...f, location_text: e.target.value }))}
              className="w-full px-3 py-2 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-sm text-white focus:outline-none focus:border-[#14F195]/50"
              data-testid="job-location"
            />
          </div>
          <div>
            <label className="block text-xs text-[#8B8FA3] mb-1">Notes</label>
            <textarea
              rows={2}
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              className="w-full px-3 py-2 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-sm text-white focus:outline-none focus:border-[#14F195]/50"
              data-testid="job-notes"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-[#8B8FA3] hover:text-white">Cancel</button>
            <button
              type="submit"
              disabled={loading || !form.customer_name.trim()}
              className="px-5 py-2 bg-[#14F195] text-[#0B0C10] rounded-lg text-sm font-semibold hover:bg-[#12D984] disabled:opacity-40"
              data-testid="job-submit"
            >
              {loading ? 'Creating...' : 'Create job'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function SOSJobs() {
  const { getJobs, createJob, completeJob, cancelJob } = useServiceOS();
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [actionLoading, setActionLoading] = useState('');

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getJobs({ status: filter || undefined });
      setJobs(res.jobs);
      setTotal(res.total);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [getJobs, filter]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const handleAction = async (action, jobId) => {
    setActionLoading(jobId);
    try {
      await action(jobId);
      await fetchJobs();
    } finally {
      setActionLoading('');
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="sos-jobs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white">Jobs</h1>
          <p className="text-sm text-[#8B8FA3]">{total} total</p>
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#14F195] text-[#0B0C10] rounded-lg text-sm font-semibold hover:bg-[#12D984]"
          data-testid="new-job-btn"
        >
          <Plus size={16} weight="bold" /> New job
        </button>
      </div>

      {/* Filter */}
      <div className="flex gap-2" data-testid="jobs-filter">
        {[
          { v: '', l: 'All' },
          { v: 'scheduled', l: 'Scheduled' },
          { v: 'completed', l: 'Completed' },
          { v: 'cancelled', l: 'Cancelled' },
        ].map(f => (
          <button
            key={f.v}
            onClick={() => setFilter(f.v)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              filter === f.v
                ? 'bg-[#6366F1]/10 text-[#6366F1] border-[#6366F1]/30'
                : 'text-[#8B8FA3] border-[#1E1F2E] hover:text-white'
            }`}
            data-testid={`jobs-tab-${f.v || 'all'}`}
          >
            {f.l}
          </button>
        ))}
      </div>

      {/* Job list */}
      {loading ? (
        <div className="flex justify-center py-16" data-testid="jobs-loading">
          <div className="w-5 h-5 border-2 border-[#14F195] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : jobs.length === 0 ? (
        <div className="text-center py-16 bg-[#12131A] border border-[#1E1F2E] rounded-xl" data-testid="jobs-empty">
          <p className="text-[#8B8FA3]">No jobs{filter ? ` with status "${filter}"` : ''}</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="jobs-list">
          {jobs.map(job => (
            <div key={job.id} className="bg-[#12131A] border border-[#1E1F2E] rounded-xl p-4" data-testid={`job-card-${job.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-semibold text-white">{job.customer_name}</h3>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_COLORS[job.status]}`}>
                      {job.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-[#8B8FA3] mb-1">
                    <span className="flex items-center gap-1">
                      <CalendarCheck size={12} /> {job.scheduled_date}
                    </span>
                    {job.start_time && (
                      <span className="flex items-center gap-1">
                        <Clock size={12} /> {job.start_time}–{job.end_time}
                      </span>
                    )}
                    {job.location_text && (
                      <span className="flex items-center gap-1">
                        <MapPin size={12} /> {job.location_text}
                      </span>
                    )}
                  </div>
                  {job.service_type && <p className="text-xs text-[#555]">{job.service_type}</p>}
                  {job.notes && <p className="text-xs text-[#555] mt-1">{job.notes}</p>}
                </div>

                {job.status === 'scheduled' && (
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => handleAction(completeJob, job.id)}
                      disabled={actionLoading === job.id}
                      className="flex items-center gap-1 px-3 py-1.5 bg-[#22C55E]/10 text-[#22C55E] rounded-lg text-xs hover:bg-[#22C55E]/20"
                      data-testid={`job-complete-${job.id}`}
                    >
                      <CheckCircle size={12} /> Complete
                    </button>
                    <button
                      onClick={() => handleAction(cancelJob, job.id)}
                      disabled={actionLoading === job.id}
                      className="flex items-center gap-1 px-3 py-1.5 bg-[#EF4444]/10 text-[#EF4444] rounded-lg text-xs hover:bg-[#EF4444]/20"
                      data-testid={`job-cancel-${job.id}`}
                    >
                      <XCircle size={12} /> Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <NewJobModal open={showNew} onClose={() => setShowNew(false)} onSubmit={async (d) => { await createJob(d); fetchJobs(); }} />
    </div>
  );
}
